// Entry-intent resolution contract for the e-filing wizard (SOR-293 — verifies
// spec requirement R1 of SPEC-SOR-292-v1).
//
// R1 pins a single, total, exclusive resolution rule: every wizard entry is
// classified as exactly one of `edit`, `fresh-start`, or `resume`, determined
// SOLELY by whether the URL carries an orderId and whether the fresh-start
// signal is present — and `urlHasOrderId` TOTALLY DOMINATES the signal (an
// entry carrying ?orderId= is `edit` even when the fresh-start signal is also
// present). This module owns both that rule (`resolveEntryIntent`) and the
// fresh-start signal contract (`FRESH_START_PARAM` + the `withFreshStartSignal`
// producer and `hasFreshStartSignal` reader) so the producer (the four launch
// sites) and the consumer (the wizard mount) never drift on the literal marker.
//
// Pure and side-effect-free: no React, router, or browser-global imports. The
// signal value stays an implementation detail behind FRESH_START_PARAM.

/** The three mutually-exclusive ways the wizard can be entered (R1). */
export type EntryIntent = 'fresh-start' | 'resume' | 'edit';

/** Inputs to the R1 resolution rule. */
export interface EntryIntentInput {
  /** The URL carries ?orderId= — opening a specific existing order. */
  urlHasOrderId: boolean;
  /** The navigation-carried fresh-start signal is present at the mount. */
  freshSignalPresent: boolean;
}

/**
 * R1: classify a wizard entry. `edit` iff `urlHasOrderId`; otherwise
 * `fresh-start` iff `freshSignalPresent`; otherwise `resume`. The early
 * `urlHasOrderId` check makes the URL dominate the signal.
 */
export function resolveEntryIntent({
  urlHasOrderId,
  freshSignalPresent,
}: EntryIntentInput): EntryIntent {
  if (urlHasOrderId) return 'edit';
  if (freshSignalPresent) return 'fresh-start';
  return 'resume';
}

/**
 * The navigation-carried marker key for the fresh-start signal. Kept behind a
 * constant so the literal value stays an implementation detail shared by the
 * producer and reader below.
 */
export const FRESH_START_PARAM = 'freshStart';

/**
 * Producer: return `path` carrying the fresh-start signal as a query param.
 * Operates as a relative-path string op (paths like `/eFiling` have no origin),
 * choosing the correct separator for a bare vs. already-queried path.
 */
export function withFreshStartSignal(path: string): string {
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${FRESH_START_PARAM}=1`;
}

/**
 * Reader: report whether the fresh-start signal is present in `params`. The
 * consumer passes `new URLSearchParams(location.search)` directly.
 */
export function hasFreshStartSignal(params: URLSearchParams): boolean {
  return params.has(FRESH_START_PARAM);
}
